import { useCallback, useState } from "react";
import type { Dashboard } from "./types";

/** Owns the inline rename editor's state so AIDashboardsContent doesn't have
 * to track editingId/draftTitle alongside its other concerns. Error state
 * is reported to the caller rather than owned here, so it can share one
 * slot with useDashboardDeletion the way the pre-split component did. */
const useDashboardRename = (
  renameDashboard: (id: string, title: string) => Promise<unknown>,
  onError: (error: Error) => void
) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");

  const cancelRename = useCallback(() => {
    setEditingId(null);
    // Blanking the draft also disarms the blur that follows an Escape.
    setDraftTitle("");
  }, []);

  const startRename = useCallback((dashboard: Dashboard) => {
    setEditingId(dashboard.id);
    setDraftTitle(dashboard.title);
  }, []);

  const saveRename = useCallback(
    async (dashboard: Dashboard) => {
      const title = draftTitle.trim();
      cancelRename();

      if (!title || title === dashboard.title) {
        return;
      }

      try {
        await renameDashboard(dashboard.id, title);
      } catch (renameError) {
        onError(renameError as Error);
      }
    },
    [draftTitle, cancelRename, renameDashboard, onError]
  );

  return {
    editingId,
    draftTitle,
    setDraftTitle,
    startRename,
    saveRename,
    cancelRename,
  };
};

export default useDashboardRename;
